import Qty from 'js-quantities';
import {
    set,
    get,
    convert,
    getSpeedUnit,
    increase,
    decrease,
    max,
    min,
    getUnitProfile,
    getUnit,
    setUnitProfiles,
} from '../helpers/UnitHelpers';

class MovementProfile {
    static unitProfiles = [];
    static unitProfile;
    static motion_profiles = [];

    /**
     * @type {number}
     */
    id = -1;

    /**
     * Type of movement the system will perform during this profile.
     * @type String
     */
    _type = '';

    /**
     * Label for the type of movement the system will perform during this profile.
     * @type String
     */
    _label = '';

    order = 0;

    listener;

    constructor() {
        this.maxVolts = 100;
        this.maxJerk = 0;
    }

    getDefault(key) {
        let profile = MovementProfile.motion_profiles.find(item => {
            return item.type === this.type;
        });

        if (profile === undefined) {
            return 0;
        }

        return profile[key];
    }

    set type(newVal) {
        if (this._type !== null && this._type.length > 0) {
            this.changed();
        }

        this._type = newVal;
    }

    get type() {
        if (
            this._type === undefined ||
            this._type === null ||
            this._type === '' ||
            this._type.length === 0
        ) {
            return 't3rd';
        }

        return this._type;
    }

    set label(newVal) {
        this._label = newVal;

        if (this._label !== null && this._label.length > 0) {
            this.changed();
        }
    }

    get label() {
        return this._label || '';
    }

    set torqueDistance(newVal) {
        let unit = this.getUnitProfile('torque_distance_system', 'rad');

        let change = true;
        if (this._torqueDistance === undefined) {
            change = false;
        }

        this._torqueDistance = set(newVal, unit);

        if (change) {
            this.changed();
        }
    }

    get torqueDistance() {
        if (this._torqueDistance === undefined) {
            this.torqueDistance = this.getDefault('moving_distance');
        }

        return get(this._torqueDistance);
    }

    increaseTorqueDistance() {
        if (this.torqueDistance === '' || this._torqueDistance === undefined) {
            let unit = this.getUnitProfile('torque_distance_system', 'rad');
            this._torqueDistance = new Qty('0 ' + unit);
        }
        this._torqueDistance = increase(this._torqueDistance);

        this.changed();
    }

    decreaseTorqueDistance() {
        if (this.torqueDistance === '' || this._torqueDistance === undefined) {
            let unit = this.getUnitProfile('torque_distance_system', 'rad');
            this._torqueDistance = new Qty('0 ' + unit);
        }
        this._torqueDistance = decrease(this._torqueDistance);

        this.changed();
    }

    set torque(newVal) {
        // let forceUnit = this.getUnitProfile('force_system', 'N');
        // let distUnit = this.getUnitProfile('distance_system', 'm');
        let unit = this.getUnitProfile('torque_system', 'N*m');

        let change = true;
        if (this._torque === undefined) {
            change = false;
        }

        this._torque = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get torque() {
        let res = get(this._torque);

        if (res === '') {
            this.torque = 0;
        }

        return res;
    }

    /**
     * Moving Distance. If no unit is passed, meters will be used as default.
     * @param newVal
     */
    set movingDistance(newVal) {
        let unit = this.getUnitProfile('distance_system', 'm');

        let change = true;
        if (this._movingDistance === undefined) {
            change = false;
        }

        this._movingDistance = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get movingDistance() {
        if (this._movingDistance === undefined) {
            this.movingDistance = this.getDefault('moving_distance');
        }

        return get(this._movingDistance);
    }

    increaseDistance() {
        if (this.movingDistance === '' || this._movingDistance === undefined) {
            let unit = this.getUnitProfile('distance_system', 'm');
            this._movingDistance = new Qty('0 ' + unit);
        }
        this._movingDistance = increase(this._movingDistance);

        this.changed();
    }

    decreaseDistance() {
        if (this.movingDistance === '' || this._movingDistance === undefined) {
            let unit = this.getUnitProfile('distance_system', 'm');
            this._movingDistance = new Qty('0 ' + unit);
        }

        this._movingDistance = decrease(this._movingDistance);
        this.changed();
    }

    /**
     * Moving Time. If no unit is passed, seconds will be used as default.
     * @param newVal
     */
    set movingTime(newVal) {
        let change = true;
        if (this._movingTime === undefined) {
            change = false;
        }

        this._movingTime = set(newVal, 's');

        // Maximize acc time newVal on moving time.
        if (this._movingTime !== undefined && this._movingTime !== null) {
            this._accTime = set(this._accTime, 's', this._movingTime.scalar / 2);
        }

        if (change) {
            this.changed();
        }
    }

    get movingTime() {
        if (this._movingTime === undefined) {
            this.movingTime = this.getDefault('moving_time');
        }

        return get(this._movingTime);
    }

    increaseMovingTime() {
        if (this.movingTime === '' || this._movingTime === undefined) {
            this._movingTime = new Qty('0 s');
        }

        this._movingTime = increase(this._movingTime);
        this.changed();
    }

    decreaseMovingTime() {
        if (this.movingTime === '' || this._movingTime === undefined) {
            this._movingTime = new Qty('0 s');
        }

        this._movingTime = decrease(this._movingTime);

        // Maximize acc time newVal on moving time.
        if (this._movingTime !== undefined && this._movingTime !== null) {
            this._accTime = set(this._accTime, 's', this._movingTime.scalar / 2);
        }
        this.changed();
    }

    /**
     * Acceleration Time. If no unit is passed, seconds will be used as default.
     * @param newVal
     */
    set accelerationTime(newVal) {
        let movingTime = this._movingTime;

        let change = true;
        if (this._accTime === undefined) {
            change = false;
        }

        if (movingTime !== undefined && movingTime !== null) {
            // Maximize acc time newVal on moving time.
            this._accTime = set(newVal, 's', movingTime.scalar / 2);
        } else {
            this._accTime = set(newVal, 's', null);
        }

        if (change) {
            this.changed();
        }
    }

    get accelerationTime() {
        if (this._accTime === undefined) {
            this.accelerationTime = this.getDefault('acceleration_time');
        }

        return get(this._accTime);
    }

    increaseAccTime() {
        if (this.accelerationTime === '' || this._accTime === undefined) {
            this._accTime = new Qty('0 s');
        }

        let movingTime = this._movingTime;
        if (movingTime !== undefined && movingTime !== null) {
            this._accTime = increase(this._accTime, 0.11, movingTime.scalar / 2);
        } else {
            this._accTime = increase(this._accTime);
        }

        this.changed();
    }

    decreaseAccTime() {
        if (this.accelerationTime === '' || this._accTime === undefined) {
            this._accTime = new Qty('0 s');
        }

        this._accTime = decrease(this._accTime);
        this.changed();
    }

    /**
     * Max Volts. If no unit is passed, volts will be used as default.
     * @param newVal
     */
    set maxVolts(newVal) {
        this._maxVolts = set(newVal, 'V');
        this.changed();
    }

    get maxVolts() {
        if (this._maxVolts === undefined) {
            this.maxVolts = set(100, 'V');
        }

        let res = get(this._maxVolts);

        if (res === '') {
            this.maxVolts = 100;
        }

        return res;
    }

    /**
     * Max Jerk. Is optional, as maxVolts can also be used (or converted to maxJerk)
     * @param newVal
     */
    set maxJerk(newVal) {
        if (this._maxJerk === undefined) {
            newVal = this.getDefault('max_jerk');
        }

        let unit = this.getUnitProfile('jerk_short', 'm/s^3');
        if (typeof newVal === 'string') {
            newVal = newVal.replace(' m/s^3', '');
        }
        this._maxJerk = set(newVal, unit);
        this.changed();
    }

    get maxJerk() {
        if (this._maxJerk === undefined) {
            let unit = this.getUnitProfile('jerk_short', 'm/s^3');
            this._maxJerk = set(this.getDefault('max_jerk'), unit);
        }

        let res = get(this._maxJerk);

        return res.replace('m/s3', 'm/s^3');
    }

    /**
     * Load. If no unit is passed, Newton will be used as default.
     * @param newVal
     */
    set load(newVal) {
        let unit = this.getUnitProfile('force_system', 'N');

        let change = true;
        if (this._load === undefined) {
            change = false;
        }

        this._load = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get load() {
        let res = get(this._load);

        if (res === '') {
            this.load = 0;
        }

        return res;
    }

    set unit(unit) {
        if (unit) {
            this._unit = unit;
        }

        this._unit = 'si';
    }

    get unit() {
        if (this._unit) {
            return this._unit;
        }

        return 'si';
    }

    set friction(newVal) {
        let unit = this.getUnitProfile('force_system', 'N');

        let change = true;
        if (this._friction === undefined) {
            change = false;
        }

        this._friction = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get friction() {
        if (this._friction === undefined) {
            this.friction = this.getDefault('friction_force');
        }
        return get(this._friction);
    }

    set maxSpeed(newVal) {
        let unit = this.getUnitProfile('speed_system', 'm/s');

        let change = true;
        if (this._maxSpeed === undefined) {
            change = false;
        }

        this._maxSpeed = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get maxSpeed() {
        if (this._maxSpeed === undefined) {
            this.maxSpeed = this.getDefault('maximum_speed');
        }

        return get(this._maxSpeed);
    }

    increaseMaxSpeed() {
        if (this.maxSpeed === '' || this._maxSpeed === undefined) {
            this.maxSpeed = 0;
        }

        this._maxSpeed = increase(this._maxSpeed);
        this.changed();
    }

    decreaseMaxSpeed() {
        if (this.maxSpeed === '' || this._maxSpeed === undefined) {
            this.maxSpeed = 0;
        }

        this._maxSpeed = decrease(this._maxSpeed);
        this.changed();
    }

    set torqueSpeed(newVal) {
        let unit = this.getUnitProfile('torque_speed_system', 'rad/s');

        let change = true;
        if (this._torqueSpeed === undefined) {
            change = false;
        }

        this._torqueSpeed = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get torqueSpeed() {
        if (this._torqueSpeed === undefined) {
            this.torqueSpeed = this.getDefault('maximum_speed');
        }

        return get(this._torqueSpeed);
    }

    increaseTorqueSpeed() {
        if (this.torqueSpeed === '' || this._torqueSpeed === undefined) {
            this.torqueSpeed = 0;
        }

        this._torqueSpeed = increase(this._torqueSpeed);
        this.changed();
    }

    decreaseTorqueSpeed() {
        if (this.torqueSpeed === '' || this._torqueSpeed === undefined) {
            this.torqueSpeed = 0;
        }

        this._torqueSpeed = decrease(this._torqueSpeed);
        this.changed();
    }

    getForceDistanceUnit(force, dist) {
        return this.getUnitProfile('torque_system', 'N*m');
    }

    set accelerationTorque(newVal) {
        if (newVal !== undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('torque_system', 'N*m');

            let change = true;
            if (this._accelerationTorque === undefined) {
                change = false;
            }

            this._accelerationTorque = set(newVal, unit);
            if (change) {
                this.changed();
            }
        }
    }

    get accelerationTorque() {
        if (this._accelerationTorque === undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('torque_system', 'N*m');

            let def = this.getDefault('acceleration_time');
            this._accelerationTorque = new Qty(def + ' ' + unit);
        }

        return get(this._accelerationTorque);
    }

    increaseAccelerationTorque() {
        if (this.accelerationTorque === '' || this._accelerationTorque === undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('torque_system', 'N*m');

            let def = this.getDefault('acceleration_time');
            this._accelerationTorque = new Qty(def + ' ' + unit);
        }

        this._accelerationTorque = increase(this._accelerationTorque);
        this.changed();
    }

    decreaseAccelerationTorque() {
        if (this.accelerationTorque === '' || this._accelerationTorque === undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('torque_system', 'N*m');

            let def = this.getDefault('acceleration_time');
            this._accelerationTorque = new Qty(def + ' ' + unit);
        }

        this._accelerationTorque = decrease(this._accelerationTorque);
        this.changed();
    }

    set acceleration(newVal) {
        if (newVal !== undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('force_system', 'N');

            let change = true;
            if (this._acceleration === undefined) {
                change = false;
            }

            this._acceleration = set(newVal, unit);
            if (change) {
                this.changed();
            }
        }
    }

    get acceleration() {
        if (this._acceleration === undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('force_system', 'N');

            let def = this.getDefault('acceleration_time');
            this._acceleration = new Qty(def + ' ' + unit);
        }

        return get(this._acceleration);
    }

    increaseAcceleration() {
        if (this.acceleration === '' || this._acceleration === undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('force_system', 'N');

            let def = this.getDefault('acceleration_time');
            this._acceleration = new Qty(def + ' ' + unit);
        }

        this._acceleration = increase(this._acceleration);
        this.changed();
    }

    decreaseAcceleration() {
        if (this.acceleration === '' || this._acceleration === undefined) {
            // let unit = this.getForceDistanceUnit('N', 'm');
            let unit = this.getUnitProfile('force_system', 'N');

            let def = this.getDefault('acceleration_time');
            this._acceleration = new Qty(def + ' ' + unit);
        }

        this._acceleration = decrease(this._acceleration);
        this.changed();
    }

    set period(newVal) {
        let unit;
        if (MovementProfile.unitProfile === undefined) {
            unit = 's';
        } else {
            unit = MovementProfile.unitProfile.time;
            if (!unit) {
                unit = 's';
            }
        }

        let change = true;
        if (this._period === undefined) {
            change = false;
        }

        this._period = set(newVal, unit);

        if (change) {
            this.changed();
        }
    }

    get period() {
        if (this._period === undefined) {
            this.period = this.getDefault('moving_distance');
        }

        return get(this._period);
    }

    increasePeriod() {
        if (this.period === '' || this._period === undefined) {
            this.period = 0;
        }

        this._period = increase(this._period);
        this.changed();
    }

    decreasePeriod() {
        if (this.period === '' || this._period === undefined) {
            this.period = 0;
        }

        this._period = decrease(this._period);
        this.changed();
    }

    isEmpty() {
        return (
            this.type === undefined ||
            this.type === null ||
            this.movingDistance === '' ||
            this.movingTime === '' ||
            this.accelerationTime === '' ||
            this.maxVolts === '' ||
            this.load === ''
        );
    }

    listen(callback) {
        this.listener = callback;
    }

    changed() {
        if (this.listener !== undefined) {
            this.listener('MovementProfile');
        }
    }

    /**
     * Convert all profile values to either SI or US units
     * @param unitSystem
     */
    convert(unitSystem = 'si') {
        this.units = getUnitProfile(unitSystem);
        if (unitSystem.indexOf('si') > -1) {
            this.load = convert(this._load, getUnit(this.units, 'force_system', 'N'));
            this.friction = convert(this._friction, getUnit(this.units, 'force_system', 'N'));
            this.torqueDistance = convert(
                this._torqueDistance,
                getUnit(this.units, 'torque_distance_system', 'rad'),
            );
            this.movingDistance = convert(
                this._movingDistance,
                getUnit(this.units, 'distance_system', 'm'),
            );
            this.acceleration = convert(
                this._acceleration,
                getUnit(this.units, 'force_system', 'N'),
            );
            this.accelerationTorque = convert(
                this._acceleration,
                getUnit(this.units, 'torque_system', 'N*m'),
            );
            this.maxSpeed = convert(this._maxSpeed, getUnit(this.units, 'speed_system', 'm/s'));
            this.torqueSpeed = convert(
                this._torqueSpeed,
                getUnit(this.units, 'torque_speed_system', 'rad/s'),
            );
            // this.acceleration = convert(this._acceleration, this.getForceDistanceUnit('N', 'm'));
        } else {
            this.load = convert(this._load, getUnit(this.units, 'force_system', 'lbf'));
            this.friction = convert(this._friction, getUnit(this.units, 'force_system', 'lbf'));
            this.movingDistance = convert(
                this._movingDistance,
                getUnit(this.units, 'distance_system', 'yd'),
            );

            let tdist = getUnit(this.units, 'torque_distance_system', 'rotation');
            if (tdist === 'revolution') {
                tdist = 'rotation';
            }

            this.torqueDistance = convert(this._torqueDistance, tdist);
            this.maxSpeed = convert(this._maxSpeed, getUnit(this.units, 'speed_system', 'm/s'));
            this.acceleration = convert(
                this._acceleration,
                getUnit(this.units, 'force_system', 'lbf'),
            );
            this.accelerationTorque = convert(
                this._acceleration,
                getUnit(this.units, 'torque_system', ''),
            );
            this.torqueSpeed = convert(
                this._torqueSpeed,
                getUnit(this.units, 'torque_speed_system', 'rpm'),
            );
            // this.acceleration = convert(this._acceleration, this.getForceDistanceUnit('lbf', 'yd'));
        }
    }

    /**
     * Create a deep copy of the object.
     * @returns {*}
     */
    copy() {
        let copy = new MovementProfile();
        copy.movingDistance = this.movingDistance;
        copy.movingTime = this.movingTime;
        copy.accelerationTime = this.accelerationTime;
        copy.acceleration = this.acceleration;
        copy.accelerationTorque = this.accelerationTorque;
        copy.period = this.period;
        copy.maxSpeed = this.maxSpeed;
        copy.load = this.load;
        copy.maxVolts = this.maxVolts;
        copy.maxJerk = this.maxJerk;
        copy.friction = this.friction;
        copy.unit = this.unit;
        copy.type = this.type;
        copy.torqueDistance = this.torqueDistance;
        copy.torque = this.torque;
        copy.torqueSpeed = this.torqueSpeed;
        copy.label = this.label;

        return copy;
    }

    getUnitProfile(key, fallback) {
        let profile = MovementProfile.unitProfile;
        if (profile !== undefined && profile[key] !== undefined) {
            if (profile[key] === 'revolution') {
                return 'rotation';
            }

            return profile[key];
        }
        return fallback;
    }

    /**
     * Create a json representation of the movement profile.
     * @returns {{moving_distance, moving_time, acc_time, max_volts, load, order}}
     */
    toJson(torque = false) {
        let cpy = this.copy();
        cpy.convert('si');

        let accelerationTypes = ['m3rd', 'msin2', 'q3rd', 'qsin2'];
        let accTimeField =
            accelerationTypes.indexOf(cpy.type.toLowerCase()) > -1
                ? torque
                    ? Qty.parse(cpy.accelerationTorque).scalar
                    : Qty.parse(cpy.acceleration).scalar
                : Qty.parse(cpy.accelerationTime).scalar;

        let mDistField;
        if (!torque) {
            mDistField =
                cpy.type.toLowerCase() === 'occ' ? cpy._period.scalar : cpy._movingDistance.scalar;
        } else {
            if (cpy._torqueDistance === undefined) {
                mDistField = 0;
            } else {
                mDistField =
                    cpy.type.toLowerCase() === 'occ'
                        ? cpy._period.scalar
                        : Qty.parse(convert(cpy._torqueDistance, 'rad')).scalar;
            }
        }

        return {
            type: this.type,
            label: this._label,
            moving_distance: mDistField,
            moving_time: cpy._movingTime.scalar,
            acceleration_time: accTimeField,
            max_voltage: cpy._maxVolts.scalar,
            max_jerk: cpy._maxJerk ? cpy._maxJerk.scalar : null,
            friction_force: torque ? Qty.parse(cpy.torque).scalar : Qty.parse(cpy.load).scalar,
            maximum_speed: torque
                ? Qty.parse(cpy.torqueSpeed).scalar
                : Qty.parse(cpy.maxSpeed).scalar,
            order: cpy._order,
        };
    }

    /**
     * Create an instance form a json object.
     * @param json
     * @returns {MovementProfile}
     */
    static fromJson(json, torque = false) {
        let profile = new MovementProfile();
        profile.id = json.id;
        profile.name = json.name;
        profile.type = json.type;
        profile.label = json.label;

        // If profile type is occ, moving_distance contains the period time.
        if (profile.type.toLowerCase() === 'occ') {
            profile.period = json.moving_distance;
        } else {
            if (torque) {
                profile.torqueDistance = json.moving_distance;
            } else {
                profile.movingDistance = json.moving_distance;
            }
        }

        // If type is one of the following, get the acceleration, else the field contains acceleration time.
        let accelerationTypes = ['m3rd', 'msin2', 'q3rd', 'qsin2'];
        if (accelerationTypes.indexOf(profile.type.toLowerCase()) > -1) {
            if (torque) {
                profile.accelerationTorque = json.acceleration_time;
            } else {
                profile.acceleration = json.acceleration_time;
            }
        } else {
            profile.accelerationTime = json.acceleration_time;
        }

        profile.movingTime = json.moving_time;
        profile.maxVolts = json.max_voltage;
        profile.maxJerk = json.max_jerk;

        if (torque) {
            profile.torqueSpeed = json.maximum_speed;
            profile.torque = json.friction_force;
        } else {
            profile.maxSpeed = json.maximum_speed;
            profile.load = json.friction_force;
        }

        profile.order = json.order;

        return profile;
    }
}

export default MovementProfile;
